/**
 * Database TypeScript definitions.
 *
 * Matches the schema in supabase/migrations/. In production these are
 * generated via `supabase gen types typescript --local` and committed
 * to the repo. Hand-authored here because we have not yet deployed the
 * schema to a Supabase project.
 *
 * Keep this file in sync with migrations/*.sql. When the schema changes,
 * regenerate this file rather than editing by hand.
 */

export type UserRole = 'admin' | 'field' | 'viewer' | 'super';

export type StageStatus =
    | 'pending'
    | 'scheduled'
    | 'in_progress'
    | 'complete'
    | 'on_hold';

export type FlatworkType = 'driveway' | 'walkway' | 'apron' | 'city_sidewalk';

// -----------------------------------------------------------------------------
// Row types (what comes out of SELECT)
// -----------------------------------------------------------------------------

export interface UserProfile {
    id: string;
    full_name: string;
    role: UserRole;
    active: boolean;
    created_at: string;
    updated_at: string;
}

export interface Stage {
    id: string;
    name: string;
    sequence_order: number;
    ppc_controlled: boolean;
    created_at: string;
}

export interface Crew {
    id: string;
    name: string;
    active: boolean;
    created_at: string;
    updated_at: string;
}

export interface Builder {
    id: string;
    name: string;
    prefix: string;
    external_ref: string | null;
    active: boolean;
    created_at: string;
    updated_at: string;
}

export interface Community {
    id: string;
    builder_id: string;
    name: string;
    external_ref: string | null;
    active: boolean;
    created_at: string;
    updated_at: string;
}

export interface Job {
    id: string;
    builder_id: string;
    community_id: string;
    lot_number: string;
    address: string | null;
    contact_name: string | null;
    contact_phone: string | null;
    active: boolean;
    flatwork_active: boolean;
    needs_review: boolean;
    review_reason: string | null;
    notes: string | null;
    fw_ready_date: string | null;
    /**
     * Builder has confirmed flatwork is actually ready, as distinct from a
     * planned FW Ready date. Renders the cell orange, matching how Andrea
     * colours the lot on her start sheet at that moment.
     */
    fw_ready_confirmed: boolean;
    /**
     * Admin override keeping a job on the active board regardless of stage or
     * flatwork completion. Set when a job was marked complete by mistake,
     * cleared when it is sent back to Completed. Avoids rewriting stage history
     * to undo a misclick, which would break that lot's weather packet window.
     */
    force_active: boolean;
    /**
     * Crew responsible for the whole job.
     *
     * Separate from job_stages.crew_id, which is per stage and stays. Andrea
     * filters by crew at close of day and exports that slice to send each crew
     * their list, and a job carrying several stage crews cannot answer "whose
     * job is this" in one column.
     */
    crew_id: string | null;
    created_at: string;
    updated_at: string;
}

export interface JobStage {
    id: string;
    job_id: string;
    stage_id: string;
    status: StageStatus;
    scheduled_date: string | null;
    actual_completion_date: string | null;
    crew_id: string | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
}

export interface FlatworkEvent {
    id: string;
    job_id: string;
    event_type: FlatworkType;
    status: StageStatus;
    scheduled_date: string | null;
    actual_completion_date: string | null;
    crew_id: string | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
}

export interface WeatherHold {
    id: string;
    job_id: string;
    stage_id: string | null;
    hold_start: string;
    hold_end: string | null;
    reason: string;
    set_by: string | null;
    set_by_email: string | null;
    cleared_by: string | null;
    cleared_by_email: string | null;
    created_at: string;
    updated_at: string;
}

export interface ActivityLogEntry {
    id: string;
    actor_id: string | null;
    actor_email: string;
    actor_role: UserRole;
    action: string;
    entity_type: string;
    entity_id: string;
    old_value: Record<string, unknown> | null;
    new_value: Record<string, unknown> | null;
    context: Record<string, unknown> | null;
    created_at: string;
}

// -----------------------------------------------------------------------------
// Composed types for UI (joined rows)
// -----------------------------------------------------------------------------

export interface JobWithContext extends Job {
    builder: Pick<Builder, 'id' | 'name' | 'prefix'>;
    community: Pick<Community, 'id' | 'name'>;
    stages: Array<JobStageWithStage>;
    /** Job level crew, joined from jobs.crew_id. Null when unassigned. */
    crew?: Pick<Crew, 'id' | 'name'> | null;
    active_hold?: WeatherHold | null;
}

export interface JobStageWithStage extends JobStage {
    stage: Stage;
    crew?: Pick<Crew, 'id' | 'name'> | null;
}

// -----------------------------------------------------------------------------
// RPC parameter / return types
// -----------------------------------------------------------------------------

export interface FieldAdvanceStageArgs {
    p_job_stage_id: string;
    p_new_status: StageStatus;
    p_completion_date?: string | null;
    p_notes?: string | null;
}

export interface FieldSetWeatherHoldArgs {
    p_job_id: string;
    p_stage_id?: string | null;
    p_reason: string;
}

export interface FieldClearWeatherHoldArgs {
    p_hold_id: string;
}

export interface ScheduleStageArgs {
    p_job_stage_id: string;
    p_scheduled_date: string;
    p_crew_id?: string | null;
}

export interface UpsertJobArgs {
    p_builder_name: string;
    p_community_name: string;
    p_lot_number: string;
    p_address?: string | null;
    p_contact_name?: string | null;
    p_contact_phone?: string | null;
    p_notes?: string | null;
    p_needs_review?: boolean;
    p_review_reason?: string | null;
}

// -----------------------------------------------------------------------------
// Bulk flatwork
// -----------------------------------------------------------------------------

export interface BulkCompleteFlatworkArgs {
    p_job_ids: string[];
    p_completion_date: string;
    /** Required for lots with no flatwork rows yet; ignored for lots that have them. */
    p_event_types?: FlatworkType[] | null;
    /** Defaults server-side to the completion date when omitted. */
    p_scheduled_date?: string | null;
}

export interface BulkCompleteFlatworkResult {
    jobs_requested: number;
    jobs_created: number;
    jobs_completed: number;
    jobs_skipped: number;
    already_complete_ids: string[];
    needed_types_ids: string[];
    rows_affected: number;
    completion_date: string;
}

export interface BulkClearFlatworkScopeResult {
    jobs_requested: number;
    jobs_cleared: number;
    refused_has_rows: string[];
}

// -----------------------------------------------------------------------------
// Canonical stage names (must match seed.sql exactly)
// -----------------------------------------------------------------------------

export const STAGE_NAMES = [
    'Pin',
    'Box',
    'Plumbing',
    'Plumbing Inspection',
    'Prep',
    'Slab Inspection',
    'Pour',
    'Complete',
] as const;

export type StageName = (typeof STAGE_NAMES)[number];

export const FLATWORK_TYPE_LABELS: Record<FlatworkType, string> = {
    driveway: 'Driveway',
    walkway: 'Walkway',
    apron: 'Apron',
    city_sidewalk: 'City Sidewalk',
};

export const STATUS_LABELS: Record<StageStatus, string> = {
    pending: 'Pending',
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    complete: 'Complete',
    on_hold: 'On Hold',
};

// -----------------------------------------------------------------------------
// Service requests (M2 - service dispatch)
// -----------------------------------------------------------------------------

export type ServiceRequestStatus =
    | 'new'
    | 'assigned'
    | 'in_progress'
    | 'complete'
    | 'cancelled';

export type ServiceRequestPriority = 'low' | 'normal' | 'urgent' | 'emergency';

export interface ServiceRequestType {
    id: string;
    code: string;
    label: string;
    description: string | null;
    active: boolean;
    display_order: number;
    created_at: string;
    updated_at: string;
}

export interface ServiceRequest {
    id: string;
    request_number: string | null;
    request_type_id: string;
    priority: ServiceRequestPriority;
    status: ServiceRequestStatus;
    subject: string;
    description: string | null;
    job_id: string | null;
    created_by: string | null;
    created_by_email: string | null;
    created_by_name: string | null;
    assigned_crew_id: string | null;
    assigned_at: string | null;
    assigned_by: string | null;
    assigned_by_email: string | null;
    completed_at: string | null;
    completed_by: string | null;
    completed_by_email: string | null;
    completion_notes: string | null;
    cancelled_at: string | null;
    cancelled_by: string | null;
    cancelled_by_email: string | null;
    cancellation_reason: string | null;
    created_at: string;
    updated_at: string;
}

// Composed types for UI (joined rows)
export interface ServiceRequestJob {
    id: string;
    lot_number: string;
    community: Pick<Community, 'name'>;
}

export interface ServiceRequestWithContext extends ServiceRequest {
    request_type: Pick<ServiceRequestType, 'id' | 'code' | 'label'>;
    assigned_crew: Pick<Crew, 'id' | 'name'> | null;
    job: ServiceRequestJob | null;
}

// RPC parameter types
export interface SubmitServiceRequestArgs {
    p_request_type_id: string;
    p_subject: string;
    p_description?: string | null;
    p_priority?: ServiceRequestPriority;
    p_job_id?: string | null;
}

export interface AssignServiceRequestArgs {
    p_request_id: string;
    p_crew_id: string;
}

export interface AdvanceServiceRequestStatusArgs {
    p_request_id: string;
    p_new_status: ServiceRequestStatus;
    p_completion_notes?: string | null;
}

export interface CancelServiceRequestArgs {
    p_request_id: string;
    p_reason: string;
}

export const SERVICE_REQUEST_STATUS_LABELS: Record<ServiceRequestStatus, string> = {
    new: 'New',
    assigned: 'Assigned',
    in_progress: 'In Progress',
    complete: 'Complete',
    cancelled: 'Cancelled',
};

export const SERVICE_REQUEST_PRIORITY_LABELS: Record<ServiceRequestPriority, string> = {
    low: 'Low',
    normal: 'Normal',
    urgent: 'Urgent',
    emergency: 'Emergency',
};
